// Adjuntos de una tarea (minutas, informes, etc.)
import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Paperclip, Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TaskAttachmentsDialog({ task, onClose, onChanged }) {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/pipelines/tasks/${task.id}/attachments`, getAuthHeaders());
    const d = await r.json();
    setItems(d.items || []);
    setLoading(false);
  }, [task.id, getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const subir = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("El archivo excede 10 MB");
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result).split(",")[1]);
        fr.onerror = rej;
        fr.readAsDataURL(file);
      });
      const r = await fetch(`${API_URL}/api/pipelines/tasks/${task.id}/attachments`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ name: file.name, size: file.size, content_type: file.type, data_b64: b64 }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Error al subir");
      toast.success("Adjunto cargado");
      load();
      onChanged && onChanged();
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };

  const descargar = async (a) => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/attachments/${a.id}/download`, getAuthHeaders());
      const d = await r.json();
      const bin = atob(d.data_b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: d.content_type || "application/octet-stream" }));
      const el = document.createElement("a");
      el.href = url; el.download = d.name || a.name; el.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="task-attachments-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><Paperclip className="w-4 h-4" /> Adjuntos de la tarea</DialogTitle>
          <DialogDescription className="text-xs truncate">{task.title}</DialogDescription>
        </DialogHeader>
        <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg py-3 cursor-pointer hover:border-[#193966] hover:bg-slate-50 text-sm text-slate-600">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? "Subiendo…" : "Subir minuta, informe u otro archivo (máx. 10 MB)"}
          <input type="file" className="hidden" onChange={(e) => { subir(e.target.files?.[0]); e.target.value = ""; }} disabled={uploading} data-testid="task-attach-input" />
        </label>
        <div className="max-h-60 overflow-y-auto divide-y border rounded-lg">
          {loading ? (
            <p className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin inline text-slate-400" /></p>
          ) : items.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">Sin adjuntos aún.</p>
          ) : items.map((a) => (
            <div key={a.id} className="flex items-center gap-2 px-3 py-2" data-testid={`task-attach-item-${a.id}`}>
              <Paperclip className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-slate-700 truncate">{a.name}</p>
                <p className="text-[10px] text-slate-400">{(a.size / 1024).toFixed(0)} KB · {a.uploaded_by_name || ""}</p>
              </div>
              <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => descargar(a)} data-testid={`task-attach-dl-${a.id}`}>
                <Download className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
